import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { Check, X, RefreshCcw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { time12h } from "../../lib/dates";

export default function AdminRefunds() {
    const [tab, setTab] = useState("requested");
    const [items, setItems] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [decisionFor, setDecisionFor] = useState(null);
    const [decisionForm, setDecisionForm] = useState({ refund_amount: "", note: "" });

    const load = async () => {
        const { data } = await api.get(`/admin/cancellations?status=${tab}`);
        setItems(data);
    };
    useEffect(() => { load(); }, [tab]); // eslint-disable-line

    const openApprove = (b) => {
        setDecisionFor({ ...b, decision: "approve" });
        setDecisionForm({ refund_amount: String(b.cancellation_suggested_refund ?? 0), note: "" });
    };
    const openDecline = (b) => {
        setDecisionFor({ ...b, decision: "decline" });
        setDecisionForm({ refund_amount: "0", note: "" });
    };

    const submitDecision = async () => {
        if (!decisionFor) return;
        setSubmitting(true);
        try {
            const path = decisionFor.decision === "approve"
                ? `/admin/cancellations/${decisionFor.id}/approve`
                : `/admin/cancellations/${decisionFor.id}/decline`;
            const payload = decisionFor.decision === "approve"
                ? { refund_amount: parseInt(decisionForm.refund_amount) || 0, note: decisionForm.note }
                : { note: decisionForm.note };
            await api.post(path, payload);
            toast.success(decisionFor.decision === "approve" ? "Refund approved" : "Cancellation declined");
            setDecisionFor(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const retryRefund = async (b) => {
        if (!window.confirm(`Retry Razorpay refund of ₹${b.refund_amount} for ${b.booking_code}?`)) return;
        try {
            const { data } = await api.post(`/admin/bookings/${b.id}/retry-refund`);
            toast.success(`Refund processed · ${data.refund_id}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Retry failed");
        }
    };

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <div className="flex items-center gap-2">
                    <RefreshCcw className="w-5 h-5 text-[#00FF66]" />
                    <h1 className="font-display text-3xl font-bold uppercase text-white">Refund Requests</h1>
                </div>
                <p className="text-slate-400 text-sm mt-1">User &lt;24h cancellations + manager-initiated cancellations need your review.</p>

                <div className="mt-5 flex gap-2">
                    {["requested", "approved", "declined"].map(t => (
                        <button key={t} onClick={() => setTab(t)} data-testid={`refund-tab-${t}`}
                            className={`px-4 h-9 rounded-full text-xs font-bold uppercase tracking-widest border ${
                                tab === t ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-900 text-slate-300 border-slate-800"
                            }`}>{t}</button>
                    ))}
                </div>

                <div className="mt-5 space-y-3">
                    {items.length === 0 && <div className="text-slate-500 text-center py-12">No {tab} cancellations.</div>}
                    {items.map(b => (
                        <div key={b.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4 sm:p-5" data-testid={`refund-row-${b.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-display font-bold text-white uppercase">{b.booking_code}</span>
                                        <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/40">
                                            Tier {b.cancellation_tier_pct ?? 0}%
                                        </span>
                                        <span className="text-[10px] uppercase tracking-widest text-slate-500">requested by {b.cancellation_requested_by_name} ({b.cancellation_requested_by_role})</span>
                                    </div>
                                    <div className="text-sm text-slate-300 mt-1">{b.user_name} · {b.centre_name} · {b.facility_name || b.turf_name}</div>
                                    <div className="text-xs text-slate-400 mt-1">{b.booking_date} · {time12h(b.start_time)}–{time12h(b.end_time)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs text-slate-400 uppercase tracking-widest">Paid</div>
                                    <div className="font-display text-2xl font-bold text-white">₹{b.paid_amount}</div>
                                    <div className="text-[11px] text-amber-400 mt-0.5">Suggested refund ₹{b.cancellation_suggested_refund ?? 0}</div>
                                </div>
                            </div>
                            {tab === "requested" && (
                                <div className="mt-3 pt-3 border-t border-slate-800 flex justify-end gap-2">
                                    <button onClick={() => openDecline(b)} data-testid={`decline-${b.id}`}
                                        className="inline-flex items-center gap-1 px-3 h-9 text-xs font-bold uppercase tracking-widest rounded border border-red-900 text-red-400 hover:bg-red-950">
                                        <X className="w-3 h-3" /> Decline
                                    </button>
                                    <button onClick={() => openApprove(b)} data-testid={`approve-${b.id}`}
                                        className="inline-flex items-center gap-1 px-3 h-9 text-xs font-bold uppercase tracking-widest rounded bg-[#00FF66] text-black hover:bg-[#00cc52]">
                                        <Check className="w-3 h-3" /> Approve & Refund
                                    </button>
                                </div>
                            )}
                            {(tab === "approved" || tab === "declined") && (
                                <div className="mt-3 pt-3 border-t border-slate-800 text-xs text-slate-500">
                                    Processed by {b.cancellation_processed_by_name} on {b.cancellation_processed_at?.slice(0, 10)}
                                    {b.refund_amount != null && tab === "approved" && <> · Refunded <span className="text-[#00FF66] font-bold">₹{b.refund_amount}</span></>}
                                    {b.cancellation_decline_reason && tab === "declined" && <> · Reason: {b.cancellation_decline_reason}</>}
                                    {/* Razorpay refund status (auto-refund) */}
                                    {tab === "approved" && b.payment_mode === "razorpay" && (
                                        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`rzp-refund-${b.id}`}>
                                            {b.razorpay_refund_id ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 text-[#00FF66] text-[10px] font-bold uppercase tracking-widest">
                                                    <CheckCircle2 className="w-3 h-3" /> Razorpay refund · {b.razorpay_refund_status || "processed"} · {b.razorpay_refund_id}
                                                </span>
                                            ) : b.razorpay_refund_status === "failed" ? (
                                                <>
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 text-[10px] font-bold uppercase tracking-widest">
                                                        <AlertTriangle className="w-3 h-3" /> Razorpay refund failed
                                                    </span>
                                                    <button onClick={() => retryRefund(b)} data-testid={`retry-refund-${b.id}`}
                                                        className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-bold uppercase tracking-widest rounded bg-amber-500/15 border border-amber-500/40 text-amber-400 hover:bg-amber-500/25">
                                                        <RefreshCcw className="w-3 h-3" /> Retry
                                                    </button>
                                                    {b.razorpay_refund_error && (
                                                        <span className="text-[10px] text-slate-500">· {b.razorpay_refund_error.slice(0, 80)}</span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Razorpay refund queued…</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {decisionFor && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3" data-testid="refund-decision-modal">
                            <h3 className="font-display text-xl font-bold uppercase text-white">{decisionFor.decision === "approve" ? "Approve refund" : "Decline cancellation"}</h3>
                            <p className="text-xs text-slate-400">{decisionFor.booking_code} · {decisionFor.user_name}</p>
                            {decisionFor.decision === "approve" && (
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Refund amount (₹)</label>
                                    <input type="number" value={decisionForm.refund_amount}
                                        onChange={(e) => setDecisionForm({ ...decisionForm, refund_amount: e.target.value })}
                                        data-testid="refund-amount-input"
                                        className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                    <div className="text-[10px] text-slate-500 mt-1">Suggested: ₹{decisionFor.cancellation_suggested_refund ?? 0} · Paid: ₹{decisionFor.paid_amount}</div>
                                </div>
                            )}
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Note (optional)</label>
                                <input value={decisionForm.note} onChange={(e) => setDecisionForm({ ...decisionForm, note: e.target.value })}
                                    data-testid="refund-note-input"
                                    className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button onClick={() => setDecisionFor(null)} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Close</button>
                                <button onClick={submitDecision} disabled={submitting} data-testid="refund-submit"
                                    className={`flex-1 h-11 font-bold uppercase tracking-widest rounded ${decisionFor.decision === "approve" ? "bg-[#00FF66] text-black" : "bg-red-500 text-white"}`}>
                                    Confirm {decisionFor.decision}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
